import { IconPlus, IconTrash } from '@tabler/icons-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  BasicDatePicker,
  BasicTable,
  ScalrBreadcrumb,
  SingleBranchSelect,
} from '@/components/customComponents'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CreateGoalPlan } from '@/screens/targets/kpiGoalPlan/CreateGoalPlan'
import { DateRange } from 'react-day-picker'

import type {
  ScalrTableColumn,
  ScalrTableData,
} from '@/components/customComponents'
import { useKpiGoalAssignments } from '@/hooks'
import { TranslationFunctionType } from '@/i18n'
import { DateType } from '@/utils/date'

const getKpiGoalPlanColumns = (
  t: TranslationFunctionType
): ScalrTableColumn[] => [
  {
    accessorKey: 'userFullName',
    header: t('targets.table.user'),
    type: 'text',
  },
  {
    accessorKey: 'kpiGroup',
    header: t('targets.table.kpiGroup'),
    type: 'text',
  },
  {
    accessorKey: 'kpiNameWithUnit',
    header: t('targets.table.kpiName'),
    type: 'text',
  },
  {
    accessorKey: 'branchName',
    header: t('targets.table.branch'),
    type: 'text',
  },
  {
    accessorKey: 'period',
    header: t('targets.table.period'),
    type: 'text',
  },
  {
    accessorKey: 'targetValue',
    header: t('targets.table.target'),
    type: 'number',
  },
  {
    accessorKey: 'bonusAmount',
    header: t('targets.table.bonus'),
    type: 'number',
  },
]

type ListSelections = {
  branch?: string // Branch name instead of Selectable
  dateRange?: DateRange
  dateFilterType?: DateType
}

const KPIGoalPlanPage = () => {
  const { t } = useTranslation()
  const [currentView, setCurrentView] = useState<'list' | 'add'>('list')
  const [listSelections, setListSelections] = useState<ListSelections>({
    branch: undefined,
    dateRange: undefined,
    dateFilterType: 'quarterly',
  })

  const { fetchKpiAssignments, kpiAssignments, loading, deleteAssignment } =
    useKpiGoalAssignments()

  // Fetch KPI assignments when branch selection changes
  useEffect(() => {
    fetchKpiAssignments(listSelections.branch || undefined)
  }, [listSelections.branch])

  // Handle date picker changes
  const handleDateChange = useCallback(
    (newDate: DateRange | undefined, newDateFilterType: DateType) => {
      setListSelections((prev) => ({
        ...prev,
        dateRange: newDate,
        dateFilterType: newDateFilterType,
      }))
    },
    []
  )

  const handleCancelCreate = useCallback(() => {
    setCurrentView('list')
  }, [])

  const handleSaveCreate = useCallback(() => {
    setCurrentView('list')
    fetchKpiAssignments(listSelections.branch || undefined)
  }, [fetchKpiAssignments, listSelections.branch])

  const handleDeleteKPI = async (id: string | number) => {
    if (typeof id !== 'string') return
    const confirmDelete = window.confirm(
      t('targets.form.confirmDeleteKpiAssignment')
    )
    if (confirmDelete) {
      try {
        await deleteAssignment(id)
      } catch (error) {
        console.error('Failed to delete KPI assignment:', error)
      }
    }
  }

  const handleBranchSelect = useCallback((branchName: string) => {
    setListSelections((prev) => ({ ...prev, branch: branchName }))
  }, [])

  const assignmentsData: ScalrTableData[] = useMemo(
    () =>
      kpiAssignments && kpiAssignments.length > 0
        ? kpiAssignments.map((assignment) => ({
            id: assignment.id,
            userFullName: assignment.userFullName,
            kpiGroup: assignment.kpiGroup,
            kpiNameWithUnit: `${assignment.kpiName} (${assignment.kpiUnit})`,
            branchName: assignment.branchName,
            period: `Q${assignment.quarter} ${assignment.year}`,
            targetValue: assignment.targetValue,
            bonusAmount: assignment.bonusAmount,
          }))
        : [],
    [kpiAssignments]
  )

  // Filter assignments based on selected date range
  const filteredAssignmentsData = useMemo(() => {
    return assignmentsData.filter((assignment) => {
      // Filter by date range (year and quarter)
      if (listSelections.dateRange?.from) {
        // Extract year and quarter from period string "Q1 2024"
        const periodMatch = assignment.period?.toString().match(/Q(\d+) (\d+)/)
        if (!periodMatch) return true

        const assignmentQuarter = parseInt(periodMatch[1])
        const assignmentYear = parseInt(periodMatch[2])

        const fromYear = listSelections.dateRange.from.getFullYear()
        const fromQuarter =
          Math.floor(listSelections.dateRange.from.getMonth() / 3) + 1

        if (listSelections.dateFilterType === 'yearly') {
          if (assignmentYear !== fromYear) {
            return false
          }
        } else if (listSelections.dateFilterType === 'quarterly') {
          if (
            assignmentYear !== fromYear ||
            assignmentQuarter !== fromQuarter
          ) {
            return false
          }
        }
      }

      return true
    })
  }, [assignmentsData, listSelections])

  return (
    <Card className="w-full min-h-screen">
      <CardHeader>
        <CardTitle pageTitle>{t('targets.navigation.kpiGoalPlan')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ScalrBreadcrumb
          breadcrumbItems={[
            { label: t('targets.navigation.targets'), href: '/targets' },
          ]}
          title={t('targets.navigation.kpiGoalPlan')}
        />

        {currentView === 'list' ? (
          <>
            {/* List View */}
            <div className="my-3">
              <Card>
                <CardContent className="p-3">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                    {/* Filter Controls */}
                    <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
                      <SingleBranchSelect
                        selectedBranch={listSelections.branch}
                        onSelectBranch={handleBranchSelect}
                      />
                      <BasicDatePicker
                        value={listSelections.dateRange}
                        dateFilterType={listSelections.dateFilterType}
                        onChange={handleDateChange}
                        noMonths
                        yearsUnselectable
                      />
                    </div>

                    {/* Action Button */}
                    <div className="flex gap-4 justify-center md:justify-start items-center">
                      <Button onClick={() => setCurrentView('add')} size="sm">
                        <IconPlus className="mr-2" />
                        {t('targets.form.addKpiGoal')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="mt-3">
              <BasicTable
                data={filteredAssignmentsData.map((item) => ({
                  ...item,
                  action: (
                    <Button
                      variant="ghost"
                      onClick={() => handleDeleteKPI(item.id as string)}
                      size="sm"
                      className="h-8 w-8 p-0 bg-white border-none"
                    >
                      <IconTrash className="text-red-500" />
                    </Button>
                  ) as any,
                }))}
                columns={[
                  ...getKpiGoalPlanColumns(t),
                  {
                    accessorKey: 'action',
                    header: t('targets.table.action'),
                    type: 'text',
                  },
                ]}
                loading={loading}
                enablePagination={true}
                itemsPerPage={6}
                showCard={true}
              />
            </div>
          </>
        ) : (
          <CreateGoalPlan
            onCancel={handleCancelCreate}
            onSave={handleSaveCreate}
            options={[]}
          />
        )}
      </CardContent>
    </Card>
  )
}

export default KPIGoalPlanPage
